import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { PolicyDocument, PolicyHeader } from './content';
import { usePolicyDocuments } from './usePolicyDocuments';
import './PolicyAcceptanceModal.scss';

type PolicyDoc = 'privacy' | 'eula';

// Document order drives the guided "Step 1 → Step 2" flow and auto-advance.
const DOC_ORDER: PolicyDoc[] = ['privacy', 'eula'];

interface DocState {
  scrolledThrough: boolean;
  atBottom: boolean;
  checked: boolean;
}

interface PolicyAcceptanceModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onLogout: () => void;
  isSaving: boolean;
  cancelLabel?: string;
}

const INITIAL_DOC_STATE: DocState = {
  scrolledThrough: false,
  atBottom: false,
  checked: false,
};

export function PolicyHeaderCard({ header }: { header: PolicyHeader }) {
  const titleLines = header.title.split('\n');
  const className = [
    'policy-modal__doc-header',
    header.uppercase ? 'policy-modal__doc-header--uppercase' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const hasDates = Boolean(header.effectiveDate || header.lastUpdated);

  return (
    <div className={className}>
      {header.kicker && (
        <p className="policy-modal__doc-kicker">{header.kicker}</p>
      )}
      <h1 className="policy-modal__doc-title">
        {titleLines.map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < titleLines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </h1>
      {hasDates && (
        <div className="policy-modal__doc-dates">
          {header.effectiveDate && (
            <span>
              <strong>Effective:</strong> {header.effectiveDate}
            </span>
          )}
          {header.lastUpdated && (
            <span>
              <strong>Last Updated:</strong> {header.lastUpdated}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function PolicyAcceptanceModal({
  isOpen,
  onAccept,
  onLogout,
  isSaving,
  cancelLabel = 'Log me out',
}: PolicyAcceptanceModalProps) {
  const { privacy, eula, loading, error, retry } = usePolicyDocuments();

  const [activeTab, setActiveTab] = useState<PolicyDoc>('privacy');
  const [docs, setDocs] = useState<Record<PolicyDoc, DocState>>({
    privacy: { ...INITIAL_DOC_STATE },
    eula: { ...INITIAL_DOC_STATE },
  });

  const privacyScrollRef = useRef<HTMLDivElement>(null);
  const eulaScrollRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const agreeBtnRef = useRef<HTMLButtonElement>(null);

  // Reset state each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('privacy');
      setDocs({
        privacy: { ...INITIAL_DOC_STATE },
        eula: { ...INITIAL_DOC_STATE },
      });
    }
  }, [isOpen]);

  // Prevent background scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap — keep keyboard navigation inside the modal
  useEffect(() => {
    if (!isOpen) return;

    const focusableSelectors =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusable = Array.from(
        modal.querySelectorAll<HTMLElement>(focusableSelectors)
      ).filter((el) => !el.closest('[inert]'));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const modal = modalRef.current;
    if (modal) {
      const firstFocusable = modal.querySelector<HTMLElement>(focusableSelectors);
      firstFocusable?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const getScrollRef = (doc: PolicyDoc) =>
    doc === 'privacy' ? privacyScrollRef : eulaScrollRef;

  // If rendered content is shorter than the scroll container, there's nothing to
  // scroll through — mark as read immediately so the checkbox isn't permanently disabled.
  useEffect(() => {
    if (!isOpen || loading || !privacy || !eula) return;
    const el = getScrollRef(activeTab).current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 4 && !docs[activeTab].scrolledThrough) {
      setDocs((prev) => ({
        ...prev,
        [activeTab]: { ...prev[activeTab], scrolledThrough: true, atBottom: true },
      }));
    }
  }, [isOpen, activeTab, docs, loading, privacy, eula]);

  const handleScroll = useCallback((doc: PolicyDoc) => {
    const el = getScrollRef(doc).current;
    if (!el) return;

    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    setDocs((prev) => ({
      ...prev,
      [doc]: { ...prev[doc], atBottom, scrolledThrough: prev[doc].scrolledThrough || atBottom },
    }));
  }, []);

  // Gentle "page down" nudge — helps less tech-savvy users discover there's
  // more content below without letting them instantly skip to the bottom.
  const nudgeScroll = (doc: PolicyDoc) => {
    const el = getScrollRef(doc).current;
    if (!el) return;
    el.scrollBy({ top: Math.round(el.clientHeight * 0.85), behavior: 'smooth' });
  };

  const handleCheckboxChange = (doc: PolicyDoc) => {
    if (!docs[doc].scrolledThrough) return;

    // The checkbox only records acceptance — moving between documents is the footer
    // button's job, so ticking a box never changes what the user is looking at.
    const willBeChecked = !docs[doc].checked;
    setDocs((prev) => ({
      ...prev,
      [doc]: { ...prev[doc], checked: willBeChecked },
    }));

    // Accepting the last document enables "Agree & Continue" — move focus there
    // (focus only; the view stays put). Defer until the disabled state has cleared.
    if (willBeChecked && DOC_ORDER.every((d) => d === doc || docs[d].checked)) {
      window.setTimeout(() => agreeBtnRef.current?.focus(), 0);
    }
  };

  // "Next" unmounts once the new tab renders, so hand focus to that document
  // rather than letting it fall back to <body>.
  const goToDoc = (doc: PolicyDoc) => {
    setActiveTab(doc);
    window.setTimeout(() => getScrollRef(doc).current?.focus(), 0);
  };

  const bothAccepted = docs.privacy.checked && docs.eula.checked;
  // The document still waiting for acceptance once the current one is accepted.
  const nextDoc = docs[activeTab].checked ? DOC_ORDER.find((d) => !docs[d].checked) : undefined;

  if (!isOpen) return null;

  const activeDocument: PolicyDocument | null =
    activeTab === 'privacy' ? privacy : eula;

  const tabLabelFor = (doc: PolicyDoc): string => {
    // Use resolved document when available, fall back to static config labels during loading.
    if (doc === 'privacy') return privacy?.tabLabel ?? 'Privacy Policy';
    return eula?.tabLabel ?? 'EULA';
  };

  const stepOf = (doc: PolicyDoc) => DOC_ORDER.indexOf(doc) + 1;

  // Friendly, step-aware guidance so non-technical users always know the next action.
  const guidance = (): { text: string; tone: 'info' | 'action' | 'done' } => {
    if (bothAccepted) {
      return {
        text: 'All set! Click "Agree & Continue" below to start using Reality Coach.',
        tone: 'done',
      };
    }
    const state = docs[activeTab];
    const label = tabLabelFor(activeTab);
    if (!state.scrolledThrough) {
      return {
        text: `Step ${stepOf(activeTab)} of ${DOC_ORDER.length}: Read the ${label} below. Reaching the end unlocks the accept box.`,
        tone: 'info',
      };
    }
    if (!state.checked) {
      return {
        text: `You've reached the end of the ${label}. Tick the box at the bottom to accept it.`,
        tone: 'action',
      };
    }
    if (nextDoc) {
      return {
        text: `${label} accepted. Click "Next" to review the ${tabLabelFor(nextDoc)}.`,
        tone: 'action',
      };
    }
    return { text: `${label} accepted.`, tone: 'done' };
  };

  const renderAcceptCard = (doc: PolicyDoc, document: PolicyDocument) => {
    const state = docs[doc];
    const accepted = state.checked;
    return (
      <div
        className={`policy-modal__accept-card${
          accepted ? ' policy-modal__accept-card--accepted' : ''
        }`}
      >
        <label
          className={`policy-modal__accept-label${
            state.scrolledThrough ? '' : ' policy-modal__accept-label--disabled'
          }`}
        >
          <input
            type="checkbox"
            checked={accepted}
            disabled={!state.scrolledThrough}
            onChange={() => handleCheckboxChange(doc)}
            aria-label={`I have read and accept the ${document.tabLabel}`}
          />
          <span>
            I have read and accept the <strong>{document.tabLabel}</strong>
          </span>
        </label>
      </div>
    );
  };

  const renderDocContent = (doc: PolicyDoc, document: PolicyDocument) => {
    const scrollRef = getScrollRef(doc);
    const showNudge = !docs[doc].atBottom;

    return (
      <div className="policy-modal__scroll-shell">
        <div
          key={doc}
          ref={scrollRef}
          className="policy-modal__scroll-area"
          onScroll={() => handleScroll(doc)}
          tabIndex={0}
          role="region"
          aria-label={`${document.tabLabel} document`}
        >
          <PolicyHeaderCard header={document.header} />
          <article className="policy-modal__content">
            <ReactMarkdown>{document.content}</ReactMarkdown>
          </article>
          {renderAcceptCard(doc, document)}
        </div>

        {showNudge && (
          <button
            type="button"
            className="policy-modal__scroll-nudge"
            onClick={() => nudgeScroll(doc)}
            aria-label="Scroll down for more"
          >
            <span className="policy-modal__scroll-nudge-text">More below</span>
            <span className="policy-modal__scroll-nudge-arrow" aria-hidden="true">
              ↓
            </span>
          </button>
        )}
      </div>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="policy-modal__state" role="status" aria-live="polite">
          <div className="policy-modal__spinner" aria-hidden="true" />
          <p>Loading policies…</p>
        </div>
      );
    }

    if (error || !privacy || !eula) {
      return (
        <div className="policy-modal__state policy-modal__state--error" role="alert">
          <p>We couldn't load the policy documents. Please check your connection and try again.</p>
          <button
            type="button"
            className="policy-modal__retry-btn"
            onClick={retry}
          >
            Try again
          </button>
        </div>
      );
    }

    const activeConfig = activeTab === 'privacy' ? privacy : eula;
    return renderDocContent(activeTab, activeConfig);
  };

  const checkboxesDisabled = !privacy || !eula;
  const guide = guidance();

  return (
    <div
      className="policy-modal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="policy-modal-title"
    >
      <div className="policy-modal__container" ref={modalRef}>
        {/* Header */}
        <div className="policy-modal__header">
          <h2 className="policy-modal__title" id="policy-modal-title">
            Welcome, please review and accept
          </h2>
          <p className="policy-modal__subtitle">
            Documents to review before you get started.
          </p>
        </div>

        {/* Tabs — numbered steps that turn into checkmarks once accepted */}
        <div className="policy-modal__tabs" role="tablist">
          {DOC_ORDER.map((doc) => {
            const accepted = docs[doc].checked;
            return (
              <button
                key={doc}
                role="tab"
                aria-selected={activeTab === doc}
                className={`policy-modal__tab${activeTab === doc ? ' policy-modal__tab--active' : ''}${
                  accepted ? ' policy-modal__tab--done' : ''
                }`}
                onClick={() => setActiveTab(doc)}
                disabled={checkboxesDisabled}
              >
                <span
                  className={`policy-modal__tab-step${
                    accepted ? ' policy-modal__tab-step--done' : ''
                  }`}
                  aria-hidden="true"
                >
                  {accepted ? '✓' : stepOf(doc)}
                </span>
                {tabLabelFor(doc)}
              </button>
            );
          })}
        </div>

        {/* Dynamic, friendly guidance banner */}
        {activeDocument && !loading && !error && (
          <div
            className={`policy-modal__guide policy-modal__guide--${guide.tone}`}
            role="status"
            aria-live="polite"
          >
            {guide.text}
          </div>
        )}

        {/* Scrollable content area */}
        <div className="policy-modal__scroll-wrapper" role="tabpanel">
          {renderBody()}
        </div>

        {/* Footer */}
        <div className="policy-modal__footer">
          <div className="policy-modal__progress" aria-hidden="true">
            <span
              className={`policy-modal__progress-item${
                docs.privacy.checked ? ' policy-modal__progress-item--done' : ''
              }`}
            >
              {docs.privacy.checked ? '✓' : '1'} Privacy Policy
            </span>
            <span className="policy-modal__progress-sep">·</span>
            <span
              className={`policy-modal__progress-item${
                docs.eula.checked ? ' policy-modal__progress-item--done' : ''
              }`}
            >
              {docs.eula.checked ? '✓' : '2'} EULA
            </span>
          </div>

          <div className="policy-modal__footer-actions">
            <button
              className="policy-modal__logout-link"
              onClick={onLogout}
              type="button"
            >
              {cancelLabel}
            </button>
            {nextDoc ? (
              <button
                className="policy-modal__agree-btn policy-modal__agree-btn--ready"
                onClick={() => goToDoc(nextDoc)}
                type="button"
              >
                Next: {tabLabelFor(nextDoc)}
              </button>
            ) : (
              <button
                ref={agreeBtnRef}
                className={`policy-modal__agree-btn${
                  bothAccepted && !isSaving ? ' policy-modal__agree-btn--ready' : ''
                }`}
                disabled={!bothAccepted || isSaving || checkboxesDisabled}
                onClick={onAccept}
                type="button"
              >
                {isSaving ? 'Saving…' : 'Agree & Continue'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
